// src/studio/components/StageRail.jsx
//
// The spine of the redesigned studio (plan D-1): Sources -> Requirements ->
// Design -> Technical Design, persistent across every project page. Design
// resolves to whichever document the project's platform actually produces
// (SFDC Solution Design or the generic HLD) via `applicableDocuments`
// (SPEC-07 D-2) — this component never hardcodes that choice.
//
// Stages are never locked: Conductor's own stage marker is advisory, not a
// gate (CLAUDE.md's vocabulary section), and the studio follows the same
// rule — a user can jump ahead to see what a later stage needs, or step back
// to fix Sources after Requirements already ran.
import PropTypes from "prop-types";
import { NavLink } from "react-router-dom";
import { CheckCircle2, Circle, Loader2, XCircle } from "lucide-react";

const STATUS_META = {
  not_started: { icon: Circle, className: "text-gray-300" },
  pending: { icon: Loader2, className: "text-amber-500 animate-spin" },
  generating: { icon: Loader2, className: "text-amber-500 animate-spin" },
  completed: { icon: CheckCircle2, className: "text-emerald-500" },
  failed: { icon: XCircle, className: "text-red-500" },
};

function StatusDot({ status }) {
  const meta = STATUS_META[status] || STATUS_META.not_started;
  const Icon = meta.icon;
  return <Icon size={14} className={`shrink-0 ${meta.className}`} />;
}

StatusDot.propTypes = { status: PropTypes.string };

/**
 * `stages`: [{ key, label, to, status }] — `status` one of STATUS_META's
 * keys, or undefined while still loading (renders as "not_started").
 */
export default function StageRail({ stages }) {
  return (
    <nav className="flex items-stretch gap-1 mb-6 border-b border-gray-200" aria-label="Document pipeline">
      {stages.map((stage, i) => (
        <NavLink
          key={stage.key}
          to={stage.to}
          className={({ isActive }) =>
            [
              "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors",
              isActive
                ? "border-brand-600 text-brand-700"
                : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300",
            ].join(" ")
          }
        >
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-xs font-semibold text-gray-500">
            {i + 1}
          </span>
          {stage.label}
          <StatusDot status={stage.status} />
        </NavLink>
      ))}
    </nav>
  );
}

StageRail.propTypes = {
  stages: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      to: PropTypes.string.isRequired,
      status: PropTypes.string,
    })
  ).isRequired,
};
